import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { shoppingApi } from '../api/shopping';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { SessionBadge, PickBadge } from '../components/ui/Badge';
import { SkeletonList } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/Toast';
import { formatDate, formatCurrency } from '../utils/format';

export function SessionDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const { data: session, isLoading: loadingSession } = useQuery({
    queryKey: ['shopping', id],
    queryFn: () => shoppingApi.getSession(id!),
    enabled: Boolean(id),
    staleTime: 60_000,
  });

  const { data: items, isLoading: loadingItems } = useQuery({
    queryKey: ['shopping', id, 'items'],
    queryFn: () => shoppingApi.getSessionItems(id!),
    enabled: Boolean(id),
    staleTime: 60_000,
  });

  const repeatMutation = useMutation({
    mutationFn: () => shoppingApi.repeatSession(id!),
    onSuccess: (newSession) => {
      queryClient.setQueryData(['shopping', 'active'], newSession);
      queryClient.invalidateQueries({ queryKey: ['shopping'] });
      navigate('/shopping/active');
      addToast(`Nova compra em ${newSession.storeName} criada!`, 'success');
    },
    onError: (err: { code?: string }) => {
      if (err.code === 'ACTIVE_SESSION_EXISTS') {
        addToast('Já existe uma compra em andamento.', 'error');
      } else {
        addToast('Erro ao repetir compra.', 'error');
      }
    },
  });

  const isLoading = loadingSession || loadingItems;

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <PageHeader title="Detalhe da Compra" showBack />
        <div className="flex-1 px-4 py-4">
          <SkeletonList count={5} />
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex flex-col">
        <PageHeader title="Detalhe da Compra" showBack />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-500">Sessão não encontrada.</p>
        </div>
      </div>
    );
  }

  const picked = items?.filter((i) => i.pickStatus === 'PICKED') ?? [];
  const skipped = items?.filter((i) => i.pickStatus === 'SKIPPED') ?? [];
  const notFound = items?.filter((i) => i.pickStatus === 'NOT_FOUND') ?? [];

  const total = picked.reduce((sum, i) => {
    const price = parseFloat(i.unitPrice ?? '0');
    return sum + (isNaN(price) ? 0 : price);
  }, 0);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 pb-[calc(4rem+env(safe-area-inset-bottom)+1.5rem)]">
      <PageHeader title={session.storeName} showBack subtitle={formatDate(session.startedAt)} />

      <div className="px-4 py-4 space-y-4">
        {/* Repeat button */}
        {session.status !== 'IN_PROGRESS' && (
          <Button
            variant="primary"
            fullWidth
            size="lg"
            onClick={() => repeatMutation.mutate()}
            loading={repeatMutation.isPending}
          >
            Repetir compra
          </Button>
        )}

        {/* Summary card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Data</p>
              <p className="font-semibold text-gray-900">{formatDate(session.startedAt)}</p>
            </div>
            <SessionBadge status={session.status} />
          </div>
          {total > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <p className="text-sm text-gray-500">Total gasto</p>
              <p className="text-2xl font-bold text-emerald-600">
                R$ {total.toFixed(2).replace('.', ',')}
              </p>
            </div>
          )}
        </div>

        {/* Picked items */}
        {picked.length > 0 && (
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 px-1">
              Comprados ({picked.length})
            </p>
            <div className="space-y-2">
              {picked.map((item) => (
                <div
                  key={item.id}
                  className="bg-white rounded-xl border border-gray-100 px-4 py-3 flex items-center justify-between gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{item.itemNameSnapshot}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Qtd: {item.quantityToBuy}</p>
                  </div>
                  {item.unitPrice ? (
                    <p className="font-bold text-gray-900 text-base flex-shrink-0">
                      {formatCurrency(item.unitPrice)}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400 flex-shrink-0">sem preço</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Skipped items */}
        {skipped.length > 0 && (
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 px-1">
              Pulados ({skipped.length})
            </p>
            <div className="space-y-2">
              {skipped.map((item) => (
                <div
                  key={item.id}
                  className="bg-gray-50 rounded-xl border border-gray-100 px-4 py-3 flex items-center justify-between"
                >
                  <p className="text-gray-400 line-through text-sm">{item.itemNameSnapshot}</p>
                  <PickBadge status="SKIPPED" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Not found items */}
        {notFound.length > 0 && (
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 px-1">
              Não encontrados ({notFound.length})
            </p>
            <div className="space-y-2">
              {notFound.map((item) => (
                <div
                  key={item.id}
                  className="bg-red-50 rounded-xl border border-red-100 px-4 py-3 flex items-center justify-between"
                >
                  <p className="text-red-600 text-sm font-medium">{item.itemNameSnapshot}</p>
                  <PickBadge status="NOT_FOUND" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
